import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Clock, Pill, Download } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseLocalDate, formatDateLongBR } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Receitas = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActive, setShowActive] = useState(true);

  useEffect(() => {
    fetchPrescriptions();
  }, [showActive]);

  const fetchPrescriptions = async () => {
    try {
      const endpoint = showActive ? '/api/prescriptions/active' : '/api/prescriptions';
      const response = await axios.get(`${API_URL}${endpoint}`);
      setPrescriptions(response.data);
    } catch (error) {
      console.error('Erro ao carregar receitas:', error);
      toast.error('Erro ao carregar receitas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      // Use parseLocalDate to avoid timezone issues
      const date = parseLocalDate(dateString);
      if (!date) return dateString;
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  if (selectedPrescription) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => setSelectedPrescription(null)}
            className="mb-4 text-primary font-medium hover:underline"
          >
            ← Voltar
          </button>

          <div className="bg-card rounded-2xl shadow-sm border border-border p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Pill className="text-primary" size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {selectedPrescription.medicamento}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Prescrito em {formatDate(selectedPrescription.data_criacao)}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">DOSAGEM</h3>
                <p className="text-lg" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {selectedPrescription.dosagem}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">HORÁRIOS</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedPrescription.horarios.map((horario, idx) => (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-secondary text-secondary-foreground rounded-full text-sm font-medium"
                    >
                      {horario}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">DURAÇÃO</h3>
                <p className="text-lg" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {selectedPrescription.duracao_dias} dias
                </p>
              </div>

              {selectedPrescription.observacoes && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">OBSERVAÇÕES</h3>
                  <p className="text-base" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {selectedPrescription.observacoes}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      selectedPrescription.ativa ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                    <span className="text-sm text-muted-foreground">
                      {selectedPrescription.ativa ? 'Receita ativa' : 'Receita inativa'}
                    </span>
                  </div>
                  {selectedPrescription.pdf_receita && (
                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedPrescription.pdf_receita;
                        link.download = `receita_${selectedPrescription.medicamento.replace(/\s+/g, '_')}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success('Download iniciado!');
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0F2B4C] text-white rounded-full hover:shadow-lg transition-shadow"
                    >
                      <Download size={18} />
                      <span className="font-medium">Baixar PDF</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Minhas Receitas
          </h2>
          <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Gerencie suas prescrições médicas
          </p>
        </div>

        <div className="flex gap-3">
          <button
            data-testid="filter-active"
            onClick={() => setShowActive(true)}
            className={`px-6 py-3 rounded-full font-medium transition-colors ${
              showActive
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            Ativas
          </button>
          <button
            data-testid="filter-all"
            onClick={() => setShowActive(false)}
            className={`px-6 py-3 rounded-full font-medium transition-colors ${
              !showActive
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            Histórico
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : prescriptions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto text-muted-foreground mb-4" size={48} />
            <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Nenhuma receita encontrada
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {prescriptions.map((prescription) => (
              <div
                key={prescription.id}
                data-testid={`prescription-card-${prescription.id}`}
                onClick={() => setSelectedPrescription(prescription)}
                className="bg-card rounded-2xl shadow-sm border border-border p-6 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Pill className="text-primary" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {prescription.medicamento}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {prescription.dosagem}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock size={16} />
                        <span>{prescription.horarios.join(', ')}</span>
                      </div>
                      <div>
                        {prescription.duracao_dias} dias
                      </div>
                    </div>
                  </div>
                  {prescription.ativa && (
                    <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      Ativa
                    </div>
                  )}
                  {prescription.pdf_receita && (
                    <div className="px-3 py-1 bg-[#0F2B4C]/10 text-[#0F2B4C] rounded-full text-xs font-medium flex items-center gap-1">
                      <Download size={12} />
                      PDF
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Receitas;